import { useDispatch, useSelector, useStore } from 'react-redux';
import type { TypedUseSelectorHook } from 'react-redux';
import { useEffect, useRef, useState, useCallback } from 'react';
import { AppDispatch, RootState, AppStore } from '.';

// Basic typed hooks
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppStore: () => AppStore = useStore;

/**
 * Hook to get authentication state
 */
export const useAuth = () => {
  return useAppSelector((state) => state.auth);
};

/**
 * Hook to check if user is authenticated
 */
export const useIsAuthenticated = (): boolean => {
  return useAppSelector((state) => !!state.auth.token);
};

/**
 * Hook for debouncing values
 */
export const useDebounce = <T,>(value: T, delay: number = 500): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Hook for WebSocket connection (for collaboration)
 */
export const useWebSocket = (url: string, enabled: boolean = true) => {
  const wsRef = useRef<WebSocket | null>(null);
  const dispatch = useAppDispatch();
  const { token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!enabled || !token) return;

    // Build WebSocket URL with token
    const wsUrl = `${url}?token=${token}`;
    
    console.log('🔌 Connecting to WebSocket:', wsUrl);
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log('✅ WebSocket connected');
      setIsConnected(true);
      dispatch({ type: 'collaboration/wsConnected' });
    };

    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('📨 WebSocket message:', data);
      dispatch({ type: 'collaboration/wsMessage', payload: data });
    };

    wsRef.current.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      setIsConnected(false);
      dispatch({ type: 'collaboration/wsError', payload: 'Connection error' });
    };

    wsRef.current.onclose = () => {
      console.log('🔌 WebSocket disconnected');
      setIsConnected(false);
      dispatch({ type: 'collaboration/wsDisconnected' });
    };

    // Cleanup on unmount
    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
  }, [url, enabled, token, dispatch]);

  const sendMessage = useCallback((message: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('⚠️ WebSocket not connected');
    }
  }, []);

  return { isConnected, sendMessage };
};

/**
 * Hook for local storage synced state
 */
export const useLocalStorage = <T,>(key: string, initialValue: T) => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error('Error reading localStorage:', error);
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      } catch (error) {
        console.error('Error setting localStorage:', error);
      }
    },
    [key, storedValue]
  );

  return [storedValue, setValue] as const;
};
